import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  EventSeat,
  Check,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  Wifi,
  AcUnit,
  LocalCafe,
  WaterDrop,
  Power,
  LocalParking,
  Security,
  MenuBook,
  Star,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const amenityIcons = {
  wifi: <Wifi />,
  ac: <AcUnit />,
  'air conditioning': <AcUnit />,
  cafeteria: <LocalCafe />,
  cafe: <LocalCafe />,
  water: <WaterDrop />,
  'drinking water': <WaterDrop />,
  power: <Power />,
  'power backup': <Power />,
  parking: <LocalParking />,
  security: <Security />,
  'reading room': <MenuBook />,
  locker: <Security />,
};

const getAmenityIcon = (amenity) => {
  const key = amenity.toLowerCase();
  return amenityIcons[key] || <Check />;
};

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

function VibrantTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const defaultDescription = `Welcome to ${library.name}! Your ultimate study destination with modern facilities and vibrant atmosphere.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  // Generate gradient from accent color
  const gradient = `linear-gradient(135deg, ${accentColor} 0%, #8B5CF6 50%, #EC4899 100%)`;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0F0F1A' }}>
      {/* Hero Section with gradient overlay */}
      <Box sx={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, rgba(139,92,246,0.8) 0%, rgba(236,72,153,0.6) 50%, rgba(15,15,26,0.9) 100%)`,
          }}
        />

        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={nextImage}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}

        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'white',
            width: '90%',
            maxWidth: 800,
          }}
        >
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              icon={<Star sx={{ color: 'white !important' }} />}
              label="PREMIUM STUDY SPACE"
              sx={{
                mb: 3,
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                fontWeight: 600,
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '4.5rem' },
                mb: 2,
                textShadow: '0 4px 30px rgba(0,0,0,0.3)',
              }}
            >
              {library.name}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 4 }}>
              <LocationOn sx={{ verticalAlign: 'middle', mr: 1 }} />
              {library.address}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={onBookNow}
              sx={{
                background: 'white',
                color: '#8B5CF6',
                fontWeight: 700,
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                borderRadius: 50,
                '&:hover': {
                  background: 'rgba(255,255,255,0.9)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s',
              }}
            >
              Book Your Spot
            </Button>
          </MotionBox>
        </Box>

        {/* Scroll indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <MotionBox
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            sx={{
              width: 30,
              height: 50,
              border: '2px solid rgba(255,255,255,0.5)',
              borderRadius: 15,
              display: 'flex',
              justifyContent: 'center',
              pt: 1,
            }}
          >
            <Box sx={{ width: 4, height: 12, bgcolor: 'white', borderRadius: 2 }} />
          </MotionBox>
        </Box>
      </Box>

      {/* Stats Section */}
      <Box sx={{ background: gradient, py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <Typography variant="h2" fontWeight={800}>{library.capacity}</Typography>
                <Typography variant="h6" sx={{ opacity: 0.8 }}>Total Seats</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <Typography variant="h2" fontWeight={800}>24/7</Typography>
                <Typography variant="h6" sx={{ opacity: 0.8 }}>Support</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <Typography variant="h2" fontWeight={800}>{library.amenities?.length || 5}+</Typography>
                <Typography variant="h6" sx={{ opacity: 0.8 }}>Amenities</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <Typography variant="h2" fontWeight={800}>100%</Typography>
                <Typography variant="h6" sx={{ opacity: 0.8 }}>Focus</Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="overline" sx={{ color: '#EC4899', letterSpacing: 3, fontWeight: 600 }}>
              ABOUT US
            </Typography>
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 3 }}>
              Your Perfect Study Sanctuary
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.9, fontSize: '1.1rem' }}>
              {library.description || defaultDescription}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
              }}
            >
              {images.slice(0, 4).map((img, idx) => (
                <Box
                  key={idx}
                  component="img"
                  src={img}
                  alt={`Gallery ${idx}`}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: '100%',
                    height: 150,
                    objectFit: 'cover',
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'transform 0.3s',
                    '&:hover': { transform: 'scale(1.05)' },
                  }}
                  onClick={() => { setCurrentImageIndex(idx); setLightboxOpen(true); }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Plans Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ color: '#8B5CF6', letterSpacing: 3, fontWeight: 600 }}>
              PRICING
            </Typography>
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
              Choose Your Plan
            </Typography>
          </Box>

          {plans.length > 0 ? (
            <Grid container spacing={4} justifyContent="center">
              {plans.map((plan, idx) => (
                <Grid item xs={12} sm={6} md={3} key={plan.id}>
                  <Card
                    sx={{
                      background: idx === 1 ? gradient : 'rgba(255,255,255,0.05)',
                      border: idx === 1 ? 'none' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 4,
                      overflow: 'visible',
                      position: 'relative',
                      transition: 'transform 0.3s',
                      '&:hover': { transform: 'translateY(-10px)' },
                    }}
                  >
                    {idx === 1 && (
                      <Chip
                        label="POPULAR"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -12,
                          right: 16,
                          bgcolor: '#EC4899',
                          color: 'white',
                          fontWeight: 700,
                        }}
                      />
                    )}
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: 'white', opacity: 0.8, mb: 2, textTransform: 'uppercase' }}>
                        {plan.plan_type.replace('_', ' ')}
                      </Typography>
                      <Typography variant="h2" sx={{ color: 'white', fontWeight: 800, mb: 3 }}>
                        ₹{plan.price_cents}
                      </Typography>
                      <Button
                        fullWidth
                        variant={idx === 1 ? 'contained' : 'outlined'}
                        onClick={onBookNow}
                        sx={{
                          bgcolor: idx === 1 ? 'white' : 'transparent',
                          color: idx === 1 ? '#8B5CF6' : 'white',
                          borderColor: 'rgba(255,255,255,0.3)',
                          fontWeight: 600,
                          py: 1.5,
                          borderRadius: 50,
                          '&:hover': {
                            bgcolor: idx === 1 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.1)',
                          },
                        }}
                      >
                        Get Started
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h5" sx={{ color: 'white', mb: 3 }}>
                Contact us for membership details
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={onBookNow}
                sx={{
                  background: gradient,
                  px: 5,
                  py: 2,
                  borderRadius: 50,
                }}
              >
                Get in Touch
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* Amenities Section */}
      {library.amenities?.length > 0 && (
        <Box sx={{ py: 10, background: 'rgba(139,92,246,0.1)' }}>
          <Container maxWidth="lg">
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, textAlign: 'center', mb: 6 }}>
              What We Offer
            </Typography>
            <Grid container spacing={3}>
              {library.amenities.map((amenity, idx) => (
                <Grid item xs={6} sm={4} md={2} key={idx}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: 3,
                      transition: 'all 0.3s',
                      '&:hover': {
                        background: gradient,
                        transform: 'translateY(-5px)',
                      },
                    }}
                  >
                    <Box sx={{ color: 'white', mb: 2 }}>
                      {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 40 } })}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                      {amenity}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Location Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 4 }}>
              Find Us
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, background: gradient }}>
                  <LocationOn sx={{ color: 'white' }} />
                </Box>
                <Typography sx={{ color: 'white' }}>{library.address}</Typography>
              </Box>
              {library.contact_phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, background: gradient }}>
                    <Phone sx={{ color: 'white' }} />
                  </Box>
                  <Typography sx={{ color: 'white' }}>
                    <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {library.contact_phone}
                    </a>
                  </Typography>
                </Box>
              )}
              {library.contact_email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, background: gradient }}>
                    <Email sx={{ color: 'white' }} />
                  </Box>
                  <Typography sx={{ color: 'white' }}>
                    <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {library.contact_email}
                    </a>
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ height: 300, borderRadius: 4, overflow: 'hidden' }}>
              {library.lat && library.lng ? (
                <iframe
                  title="Location"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
                  loading="lazy"
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: 'rgba(255,255,255,0.05)' }}>
                  <Typography color="rgba(255,255,255,0.5)">Map unavailable</Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ py: 10, background: gradient, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
            Ready to Level Up Your Studies?
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 4, fontSize: '1.1rem' }}>
            Join {library.name} and experience the perfect study environment
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onBookNow}
            sx={{
              bgcolor: 'white',
              color: '#8B5CF6',
              fontWeight: 700,
              px: 6,
              py: 2,
              borderRadius: 50,
              fontSize: '1.1rem',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
            }}
          >
            Book Now
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, textAlign: 'center', bgcolor: '#0A0A12' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>
          Powered by{' '}
          <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: '#8B5CF6' }}>
            BookMyLibrary
          </a>
        </Typography>
      </Box>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: 'black' } }}>
        <IconButton onClick={() => setLightboxOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: 'white', zIndex: 1 }}>
          <Close />
        </IconButton>
        <Box component="img" src={images[currentImageIndex]} alt="Gallery" loading="lazy" decoding="async" sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
      </Dialog>
    </Box>
  );
}

export default VibrantTheme;
